import React from "react";
import { Header, Logo, LogoThemes } from "@hid-galaxy-ui/galaxy-react";
import { ClickAwayListener, useMediaQuery } from "@mui/material";
import Popper from "@mui/material/Popper";
import useLogoutHandler from "../../../hooks/useLogoutHandler";
import { useAppDispatch, useAppSelector } from "../../../redux";
import MiniCart from "../MiniCart";
import { useLocation, useNavigate, useParams } from "react-router";
import { fetchCartSagaActions } from "../../../redux/saga/cart/saga-actions";
import NotificationPopper from "../NotificationPopper";
import { getNotificationListSagaAction } from "../../../redux/saga/notification/saga-actions";
import { usePartnerSessionCanAccessModule } from "../../../hooks/usePartnerSessionPermissions";
import { useIsHidStaff } from "../../../hooks/useIsHidStaff";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../utils/permissions/partnerPortalPermissionKeys";
import "./HIDHeader.scss";

const HIDHeader = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [cartOpen, setCartOpen] = React.useState<boolean>(false);
  const [anchorElNotification, setAnchorElNotification] =
    React.useState<HTMLElement | null>(null);
  const [notificationOpen, setNotificationOpen] =
    React.useState<boolean>(false);
  const [triggerLogout] = useLogoutHandler();
  const { cartSize } = useAppSelector((state) => state.cart);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { notificationList } = useAppSelector((state) => state.notification);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const pathNameArray = pathName.split("/");
  const { partnerID } = useParams();
  const isAdminUser = useIsHidStaff();
  const isPartnerPortal = pathNameArray[1] === "partner";
  const isResourcesPage = pathNameArray[1] === "resources";
  const partnerHomeId = partnerID || loggedUserDetail?.metadata?.partner;
  const usePartnerChrome =
    isPartnerPortal || (isResourcesPage && !isAdminUser);
  const cartCheckoutAccess = usePartnerSessionCanAccessModule(
    PP_KEYS.CART_CHECKOUT
  );
  const open = Boolean(anchorEl);
  const id = open ? "cart-popper" : undefined;
  const openNotification = Boolean(anchorElNotification);
  const idNotification = openNotification ? "notification-popper" : undefined;
  const isMobile = useMediaQuery("(max-width:1023px)");

  React.useEffect(() => {
    if (loggedUserDetail?.metadata?.cart_token) {
      dispatch(fetchCartSagaActions(loggedUserDetail.metadata.cart_token));
      localStorage.setItem("cartId", loggedUserDetail.metadata.cart_token);
    } else {
      localStorage.removeItem("cartId");
    }
    if (isAdminUser) dispatch(getNotificationListSagaAction());
  }, [loggedUserDetail]);

  const onActionClick = (
    e:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.ChangeEvent<HTMLElement>,
    key: string
  ) => {
    if (key === "chatbot") navigate(`/partner/${partnerID}/chatbot`);
    if (key === "cartShopping") handleCartClick(e);
    if (key === "notification") handleNotificationClick(e);
    if (key === "my-account")
      navigate(
        isAdminUser
          ? "/hid-admin/my-account"
          : `/partner/${partnerHomeId}/my-account`
      );
    if (key === "logout") triggerLogout();
    if (key === "hid-documentation")
      window.open("/nitgeo/HomeTarget/home.htm", "_blank");
  };

  const handleCartClick = (
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.ChangeEvent<HTMLElement>
  ) => {
    if (!anchorEl && !cartOpen)
      setTimeout(() => {
        setCartOpen(true);
      }, 100);
    setAnchorEl(event.currentTarget);
  };

  const handleCartClose = () => {
    if (anchorEl && cartOpen) {
      setAnchorEl(null);
      setCartOpen(false);
    }
  };

  const handleNotificationClick = (
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.ChangeEvent<HTMLElement>
  ) => {
    if (!anchorElNotification && !notificationOpen)
      setTimeout(() => {
        setNotificationOpen(true);
      }, 100);
    setAnchorElNotification(event.currentTarget);
  };

  const handleNotificationClose = () => {
    if (anchorElNotification && notificationOpen) {
      setAnchorElNotification(null);
      setNotificationOpen(false);
    }
  };

  const homeURL = usePartnerChrome
    ? `/partner/${partnerHomeId}/dashboard/home`
    : "/hid-admin/home";
  const headerTitle = isMobile
    ? ""
    : usePartnerChrome
    ? "Partner Portal"
    : "Partner Portal Administration";
  const userName = isMobile ? "" : loggedUserDetail?.first_name;

  const partnerPortalActions = [
    ...(cartCheckoutAccess
      ? [
          {
            icon: "cartShopping",
            key: "cartShopping",
            badge: cartSize,
          },
        ]
      : []),
    {
      icon: "circleUser",
      key: "user",
      isUserAction: true,
      subNavItems: [
        {
          data: [
            {
              key: "my-account",
              value: "My Account",
            },
            {
              key: "logout",
              value: "Sign Out",
            },
          ],
        },
      ],
    },
  ];

  const actionsArray = usePartnerChrome
    ? partnerPortalActions
    : [
        {
          icon: "bell",
          key: "notification",
          badge: notificationList.length,
        },
        {
          icon: "circleUser",
          key: "user",
          isUserAction: true,
          subNavItems: [
            {
              data: [
                {
                  key: "my-account",
                  value: "My Account",
                },
                // {
                //   key: "hid-documentation",
                //   value: "HID Documentation",
                // },
                {
                  key: "logout",
                  value: "Sign Out",
                },
              ],
            },
          ],
        },
      ];

  const actionDropDownConfig =
    isAdminUser && isPartnerPortal
      ? {
          actionDropDownConfig: {
            key: "actionDropDown",
            options: [
              { label: "Change Partner", value: "change-partner" },
              { label: "Manage Partner", value: "manage-partner" },
            ],
            placeholder: partnerDetails.partner_name,
            onChange: (option: any) => {
              if (option.value === "change-partner")
                navigate("/hid-admin/home");
              else
                navigate(
                  `/hid-admin/dashboard/partner-organizations/${partnerDetails.ID}/partner-details`
                );
            },
          },
        }
      : {};
  return (
    <>
      <Header
        originHref={homeURL}
        logo={
          <span
            style={{ display: "flex" }}
            onClick={(e) => {
              e.preventDefault();
              navigate(homeURL);
            }}
          >
            <Logo theme={LogoThemes.Light} title={headerTitle} />
          </span>
        }
        {...actionDropDownConfig}
        userName={userName}
        actions={actionsArray}
        actionCallback={onActionClick}
      />
      <ClickAwayListener onClickAway={handleCartClose}>
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          sx={{ zIndex: 5 }}
        >
          <MiniCart />
        </Popper>
      </ClickAwayListener>
      <ClickAwayListener onClickAway={handleNotificationClose}>
        <Popper
          id={idNotification}
          open={openNotification}
          anchorEl={anchorElNotification}
          placement="bottom-end"
          sx={{ zIndex: 5 }}
        >
          <NotificationPopper />
        </Popper>
      </ClickAwayListener>
    </>
  );
};

export default HIDHeader;
